import { BarChart2, FileText, RefreshCw } from 'lucide-react';
import { Link } from 'wouter';
import { nextActions, type NextAction } from '../../lib/dashboard/overview';
import { routes } from '../../lib/assessment/routes';
import type { FreeResult } from '../../lib/apiClient';
import { Button } from '../ui/button';

/**
 * The Overview tab's **Next Best Action** list, copied from production.
 *
 * The section is absent entirely when there is nothing to suggest — production
 * renders it only when the list is non-empty, and a customer who has taken the
 * Bottleneck assessment without unlocking it has an empty list on purpose,
 * because the unlock is offered further down the page.
 *
 * The first entry carries production's emphasis; every later one is a plain
 * card.
 *
 * PARITY TODO: production's report links are `PrefetchLink`s that warm the
 * route's chunk on hover. V2 has no prefetching layer, so these are plain
 * links — the same destination, without the warm-up.
 */
export function NextBestAction({
  assessments,
  now = new Date(),
}: {
  assessments: FreeResult[];
  /** Injectable so the thirty-day branch can be asserted at a fixed date. */
  now?: Date;
}) {
  const actions = nextActions(assessments, now);
  if (actions.length === 0) return null;

  return (
    <section data-testid="next-best-action">
      <h2 className="text-xl font-semibold mb-4">Next Best Action</h2>
      <div className="space-y-3">
        {actions.map((action, idx) => (
          <div
            key={`${action.action}-${idx}`}
            data-testid="next-action"
            data-action={action.action}
            className={`p-5 rounded-xl border flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
              idx === 0 ? 'border-primary/30 bg-primary/5' : 'border-border bg-card'
            }`}
          >
            <p className="text-sm leading-relaxed">{action.label}</p>
            <div className="shrink-0">
              <ActionControl action={action} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ActionControl({ action }: { action: NextAction }) {
  switch (action.action) {
    case 'take-bottleneck':
      return (
        <ActionCta href={routes.assessment('bottleneck')} testId="next-action-take-bottleneck">
          <Button size="sm" tabIndex={-1}>
            Take Assessment
          </Button>
        </ActionCta>
      );

    case 'take-reassessment':
      return (
        <ActionCta href={routes.assessment('bottleneck')} testId="next-action-take-reassessment">
          <Button size="sm" className="gap-2" tabIndex={-1}>
            <RefreshCw className="w-3.5 h-3.5" aria-hidden="true" />
            Take Reassessment
          </Button>
        </ActionCta>
      );

    case 'open-report':
      return (
        <ActionCta href={routes.report(action.resultId ?? '')} testId="next-action-open-report">
          <Button size="sm" variant="outline" className="gap-2" tabIndex={-1}>
            <FileText className="w-3.5 h-3.5" aria-hidden="true" />
            Open Report
          </Button>
        </ActionCta>
      );

    case 'view-work-fit-result':
      return (
        <ActionCta href={routes.result(action.resultId ?? '')} testId="next-action-view-work-fit">
          <Button size="sm" variant="outline" className="gap-2" tabIndex={-1}>
            <BarChart2 className="w-3.5 h-3.5" aria-hidden="true" />
            View Work Fit Result
          </Button>
        </ActionCta>
      );

    case 'explore-work-fit':
      return (
        <ActionCta href={routes.workFit} testId="next-action-explore-work-fit">
          <Button size="sm" variant="outline" tabIndex={-1}>
            Explore Work Fit
          </Button>
        </ActionCta>
      );

    case 'open-work-fit-report':
      return (
        <ActionCta
          href={routes.report(action.resultId ?? '')}
          testId="next-action-open-work-fit-report"
        >
          <Button size="sm" variant="outline" className="gap-2" tabIndex={-1}>
            <FileText className="w-3.5 h-3.5" aria-hidden="true" />
            Open Work Fit Report
          </Button>
        </ActionCta>
      );
  }
}

/**
 * The control's anchor. Named `Cta` rather than `Link` deliberately: the visual
 * system's scanner treats every `*Link` element as a link site and asserts the
 * focus ring on it directly, and this is a component that renders one rather
 * than being one. The ring is on the `Link` below, where the scanner reads it.
 */
function ActionCta({
  href,
  testId,
  children,
}: {
  href: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <Link
      href={href}
      data-testid={testId}
      className="focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
    >
      {children}
    </Link>
  );
}
